<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold text-gray-900 mb-2">Mina Biljetter</h1>
        <p class="text-gray-600 mb-4">Här ser du alla biljetter du har köpt.</p>

      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="text-center">
          <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p class="text-gray-600">Laddar dina biljetter...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div class="text-red-500 mb-4">
          <svg class="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-red-800 mb-2">Ett fel uppstod</h3>
        <p class="text-red-700 mb-4">{{ error }}</p>
        <button 
          @click="fetchTickets"
          class="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium"
        >
          Försök igen
        </button>
      </div>

      <!-- Empty State -->
      <div v-else-if="tickets.length === 0" class="text-center py-16 bg-white rounded-xl shadow-lg border border-gray-100">
        <div class="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path>
          </svg>
        </div>
        <h3 class="text-xl font-semibold text-gray-900 mb-2">Inga biljetter än</h3>
        <p class="text-gray-600 mb-6">Du har inte köpt några biljetter ännu.</p>

      </div>

      <!-- Tickets Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          v-for="ticket in tickets" 
          :key="ticket._id"
          class="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
        >
          <!-- Ticket Header -->
          <div class="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 text-white">
            <div class="flex items-center justify-between mb-2">
              <h3 class="text-lg font-semibold">{{ ticket.eventName }}</h3>
              <span class="bg-white bg-opacity-20 px-2 py-1 rounded-full text-xs font-medium">
                {{ ticket.ticketType }}
              </span>
            </div>
            <p class="text-indigo-100 text-sm">{{ formatDate(ticket.eventDate) }}</p>
          </div>

          <!-- Ticket Content -->
          <div class="p-6">
            <!-- Ticket Details -->
            <div class="space-y-3 mb-6">
              <div class="flex items-center gap-2 text-sm">
                <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
                <span class="text-gray-600">{{ ticket.buyerName }}</span>
              </div>
              <div class="flex items-center gap-2 text-sm">
                <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                <span class="text-gray-600">{{ ticket.eventLocation }}</span>
              </div>
              <div class="flex items-center gap-2 text-sm">
                <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                </svg>
                <span class="text-gray-600">{{ ticket.ticketPrice }} SEK</span>
              </div>
            </div>

            <!-- QR Code -->
            <div class="text-center mb-6">
              <div class="bg-gray-50 rounded-lg p-4 inline-block">
                <img 
                  :src="getQRCodeDataURL(ticket._id)" 
                  :alt="`QR Code for ${ticket.eventName}`"
                  class="w-32 h-32 mx-auto"
                />
              </div>
              <p class="text-xs text-gray-500 mt-2">Visa denna QR-kod vid entrén</p>
            </div>

            <!-- Ticket Status -->
            <div class="mb-4">
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-600">Status:</span>
                <span 
                  :class="[
                    'px-2 py-1 rounded-full text-xs font-medium',
                    ticket.isScanned 
                      ? 'bg-red-100 text-red-800' 
                      : 'bg-green-100 text-green-800'
                  ]"
                >
                  {{ ticket.isScanned ? 'Redan skannad' : 'Giltig' }}
                </span>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex flex-col gap-2">
              <button 
                @click="resendEmail(ticket._id)"
                :disabled="resending === ticket._id"
                class="w-full bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                <span v-if="resending === ticket._id" class="animate-spin mr-2">⟳</span>
                Skicka biljett via email
              </button>
              <button 
                @click="downloadTicket(ticket)"
                class="w-full bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
              >
                Ladda ner biljett
              </button>
            </div>
            
            <!-- Test Email Button (for debugging) -->
            <div class="mt-2">
              <button 
                @click="testEmail"
                class="w-full bg-yellow-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-yellow-600 transition-colors"
              >
                🧪 Testa Email (Debug)
              </button>
            </div>
            
            <!-- Email Status Info -->
            <div class="mt-4 p-3 bg-blue-50 rounded-lg">
              <div class="flex items-center gap-2 text-sm text-blue-700">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
                <span>Email skickad till: {{ ticket.buyerEmail }}</span>
              </div>
              <p class="text-xs text-blue-600 mt-1">
                Kontrollera din spam-mapp om du inte fick emailen
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import QRCode from 'qrcode';
import jsPDF from 'jspdf';

const tickets = ref([]);
const loading = ref(true);
const error = ref('');
const resending = ref(null);
const qrCodes = ref({});

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('sv-SE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const generateQRCode = async (qrCode) => {
  try {
    const qrData = JSON.stringify({
      qrCode: qrCode
    });
    return await QRCode.toDataURL(qrData);
  } catch (error) {
    console.error('Error generating QR code:', error);
    return '';
  }
};

const getQRCodeDataURL = (ticketId) => {
  return qrCodes.value[ticketId] || '';
};

const fetchTickets = async () => {
  try {
    loading.value = true;
    error.value = '';
    
    const response = await axios.get('/api/tickets/my-tickets');
    tickets.value = response.data;
    
    // Generate QR codes for all tickets
    for (const ticket of tickets.value) {
      if (ticket.qrCode) {
        const qrCodeDataURL = await generateQRCode(ticket.qrCode);
        qrCodes.value[ticket._id] = qrCodeDataURL;
      }
    }
    
  } catch (err) {
    console.error('Error fetching tickets:', err);
    error.value = 'Kunde inte ladda dina biljetter. Försök igen senare.';
  } finally {
    loading.value = false;
  }
};

const resendEmail = async (ticketId) => {
  try {
    resending.value = ticketId;
    
    await axios.post(`/api/tickets/resend-email/${ticketId}`);
    
    // Show success message
    alert('✅ Biljett skickad via email! Kontrollera din inbox och spam-mapp.');
    
  } catch (err) {
    console.error('Error resending email:', err);
    
    let errorMessage = 'Kunde inte skicka biljett via email.';
    if (err.response?.status === 404) {
      errorMessage = 'Biljetten kunde inte hittas.';
    } else if (err.response?.status === 403) {
      errorMessage = 'Du har inte behörighet att skicka om denna biljett.';
    } else if (err.response?.status === 500) {
      errorMessage = 'Serverfel. Kontrollera din internetanslutning och försök igen.';
    }
    
    alert(`❌ ${errorMessage} Försök igen senare.`);
  } finally {
    resending.value = null;
  }
};

const testEmail = async () => {
  try {
    console.log('🧪 Testing email functionality...');
    
    const response = await axios.post('/api/tickets/test-email');
    
    console.log('✅ Test email response:', response.data);
    alert('🧪 Test email skickad! Kontrollera serverns konsol för detaljer.');
    
  } catch (err) {
    console.error('❌ Error testing email:', err);
    
    let errorMessage = 'Kunde inte testa email-funktionaliteten.';
    if (err.response?.data?.error) {
      errorMessage += ` Fel: ${err.response.data.error}`;
    }
    
    alert(`❌ ${errorMessage} Kolla serverns konsol för mer information.`);
  }
};

const downloadTicket = async (ticket) => {
  try {
    // Create new PDF document
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    // Set background color (blue)
    pdf.setFillColor(79, 70, 229); // #4f46e5
    pdf.rect(0, 0, 210, 297, 'F');
    
    // Set text color to white
    pdf.setTextColor(255, 255, 255);
    
    // Header
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('BILJETT', 105, 30, { align: 'center' });
    
    // Line under header
    pdf.setDrawColor(255, 255, 255);
    pdf.setLineWidth(1);
    pdf.line(70, 35, 140, 35);
    
    // QR Code Section - LÄNGST UPP
    let yPos = 50;
    pdf.setFontSize(12);
    pdf.text('QR-kod för entrén:', 105, yPos, { align: 'center' });
    yPos += 8;
    
    // Add QR code image if available
    try {
      const qrCodeDataURL = getQRCodeDataURL(ticket._id);
      if (qrCodeDataURL) {
        // Convert data URL to base64
        const base64 = qrCodeDataURL.split(',')[1];
        pdf.addImage(base64, 'JPEG', 75, yPos, 60, 60);
        yPos += 70;
      }
    } catch (error) {
      console.error('Error adding QR code:', error);
      yPos += 20;
    }
    
    // Event Info Section
    yPos += 10;
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text(ticket.eventName, 105, yPos, { align: 'center' });
    
    // Event details
    yPos += 15;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    
    const lineHeight = 8;
    
    pdf.text('Datum:', 20, yPos);
    pdf.text(formatDate(ticket.eventDate), 60, yPos);
    yPos += lineHeight;
    
    pdf.text('Plats:', 20, yPos);
    pdf.text(ticket.eventLocation, 60, yPos);
    yPos += lineHeight;
    
    pdf.text('Pris:', 20, yPos);
    pdf.text(`${ticket.ticketPrice} SEK`, 60, yPos);
    yPos += lineHeight;
    
    pdf.text('Typ:', 20, yPos);
    pdf.text(ticket.ticketType, 60, yPos);
    yPos += lineHeight + 10;
    
    // Buyer Info Section
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Kopare', 105, yPos, { align: 'center' });
    yPos += lineHeight;
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    
    pdf.text('Namn:', 20, yPos);
    pdf.text(ticket.buyerName, 60, yPos);
    yPos += lineHeight;
    
    pdf.text('Email:', 20, yPos);
    pdf.text(ticket.buyerEmail, 60, yPos);
    yPos += lineHeight + 10;
    
    // Footer
    yPos += 10;
    pdf.setFontSize(10);
    pdf.text(`Genererad: ${new Date().toLocaleDateString('sv-SE')}`, 105, yPos, { align: 'center' });
    yPos += lineHeight;
    pdf.text(`Biljett-ID: ${ticket._id}`, 105, yPos, { align: 'center' });
    
    // Save the PDF
    const filename = `biljett-${ticket.eventName.replace(/[^a-zA-Z0-9]/g, '-')}-${ticket._id}.pdf`;
    pdf.save(filename);
    
    // Show success message
    alert('✅ PDF-biljett laddas ner!');
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('❌ Kunde inte generera PDF. Försök igen senare.');
  }
};

onMounted(() => {
  fetchTickets();
});
</script>
